import { useState, useEffect, useContext, useRef } from 'react';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';
import { ThemeContext } from '../Context';
import GetUserAvatar from '../tool/GetUserAvatar';
import { GuardiaAccessChecker } from './GuardiaAccessChecker';
import axios from 'axios';
import { logAction, formatReportNumber, contentLengthLog } from '../../utils/logClient';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  Divider,
  CircularProgress,
  Paper,
  Fade,
  Alert
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function Chat() {
  const { report } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket } = useContext(SocketContext);
  const { theme } = useContext(ThemeContext);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [listenError, setListenError] = useState('');
  const [guardiaAccess, setGuardiaAccess] = useState({ hasAccess: false, reason: '' });
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // Vérifier l'accès GuardIA au chargement
  useEffect(() => {
    const checkGuardiaAccess = async () => {
      const access = await GuardiaAccessChecker.checkFeatureAccess('listen');
      setGuardiaAccess(access);
    };
    
    checkGuardiaAccess();
  }, []);

  // Charger les messages existants
  useEffect(() => {
    if (!report) return;

    const fetchMessages = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/report/search`, {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data && response.data.chat) {
          setMessages(response.data.chat);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des messages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [report]);

  // Écouter les nouveaux messages
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (data) => {
      setMessages((prevMessages) => [...prevMessages, data]);
    };

    socket.on('newMessage', handleNewMessage);

    return () => {
      socket.off('newMessage', handleNewMessage);
    };
  }, [socket]);

  // Faire défiler automatiquement vers le dernier message (nouveaux messages)
  useEffect(() => {
    if (!loading && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Scroll instantané vers le bas quand le chargement initial est terminé
  useEffect(() => {
    if (!loading && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
    }
  }, [loading]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (error) {
          console.warn('Erreur lors de l\'arrêt du MediaRecorder :', error);
        }
      }

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => {
          try {
            track.stop();
          } catch (error) {
            console.warn('Erreur lors de l\'arrêt d\'une piste audio :', error);
          }
        });
        audioStreamRef.current = null;
      }
    };
  }, []);

  const appendTranscriptToMessage = (transcript) => {
    setMessage(prev => {
      const trimmedPrev = prev.trim();
      const combined = trimmedPrev ? `${trimmedPrev} ${transcript}` : transcript;
      return combined.trim();
    });

    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 0);
  };

  const guardiaListen = async (audioBlob) => {
    if (!audioBlob || audioBlob.size === 0) {
      setListenError('Aucun son détecté. Veuillez réessayer.');
      return;
    }

    setListenError('');
    setIsTranscribing(true);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'report-chat-recording.webm');
      formData.append('language', 'fr');

      const token = sessionStorage.getItem('authToken');
      const response = await fetch('/api/guardia/listen', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      let data = null;
      try {
        const clonedResponse = response.clone();
        data = await response.json();
        if (!response.ok && clonedResponse) {
          const rawText = await clonedResponse.text();
          if (rawText) {
            console.error('Réponse brute guardiaListen (chat):', rawText);
          }
        }
      } catch (jsonError) {
        console.error('Réponse JSON invalide de guardiaListen (chat):', jsonError);
        try {
          const rawText = await response.text();
          if (rawText) {
            console.error('Réponse brute guardiaListen (chat) après échec JSON:', rawText);
          }
        } catch (rawError) {
          console.error('Impossible de lire la réponse brute guardiaListen (chat):', rawError);
        }
      }

      if (!response.ok || !data || !data.success) {
        const errorMessage = data?.message || data?.error || 'Impossible de transcrire l\'audio.';
        throw new Error(errorMessage);
      }

      const transcript = (data.transcript || '').trim();
      if (transcript) {
        appendTranscriptToMessage(transcript);
      } else {
        setListenError('Transcription vide. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Erreur de transcription GuardIA (chat):', error);
      setListenError(error.message || 'Erreur inattendue lors de la transcription.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const stopAudioStream = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (error) {
          console.warn('Erreur lors de l\'arrêt d\'une piste audio :', error);
        }
      });
      audioStreamRef.current = null;
    }
  };

  const stopListening = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      setIsListening(false);
      try {
        recorder.stop();
      } catch (error) {
        console.warn('Erreur lors de l\'arrêt du MediaRecorder (chat):', error);
      }
    } else {
      setIsListening(false);
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      stopAudioStream();
    }
  };

  const startListening = async () => {
    if (!guardiaAccess.hasAccess) {
      setListenError(guardiaAccess.reason);
      return;
    }

    if (isListening || isTranscribing) {
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setListenError('La capture audio n\'est pas supportée par ce navigateur.');
      return;
    }

    try {
      setListenError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      });

      mediaRecorder.addEventListener('error', (event) => {
        console.error('Erreur MediaRecorder (chat):', event.error);
        setListenError(event.error?.message || 'Erreur lors de l\'enregistrement audio.');
        setIsListening(false);
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        stopAudioStream();
      });

      mediaRecorder.addEventListener('stop', () => {
        setIsListening(false);
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
        stopAudioStream();

        if (audioBlob.size === 0) {
          setListenError('Aucun audio détecté. Veuillez réessayer.');
          return;
        }

        guardiaListen(audioBlob);
      });

      mediaRecorder.start();
      setIsListening(true);
    } catch (error) {
      console.error('Erreur lors de l\'activation du micro GuardIA (chat):', error);
      stopAudioStream();
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      setIsListening(false);

      if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
        setListenError('Accès au micro refusé. Veuillez autoriser l\'accès pour utiliser la dictée.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        setListenError('Aucun micro n\'a été détecté.');
      } else {
        setListenError(error.message || 'Impossible de démarrer l\'enregistrement audio.');
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !report || !user || !socket) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = `${report.number}-${report.year}`;
      
      // Envoyer le message via WebSocket
      socket.emit('sendMessage', {
        reportId,
        userId: user,
        message: message.trim()
      });

      // Sauvegarder dans la base de données en utilisant number et year
      await axios.post(
        `/api/report/chat/${report.number}/${report.year}`,
        {
          userId: user,
          message: message.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Log du message chat
      logAction('Message chat envoyé', formatReportNumber(report.number, report.year), {
        ...contentLengthLog(message.trim())
      });
      
      setMessage('');
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    // Pour les messages plus anciens, afficher la date complète
    return format(date, 'dd/MM/yyyy HH:mm', { locale: fr });
  };

  const toggleMicrophone = () => {
    if (!guardiaAccess.hasAccess) {
      alert(guardiaAccess.reason);
      return;
    }
    
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: isDark ? '#1a252f' : '#f8f9fa',
      }}
    >
      {/* Zone des messages */}
      <Box 
        ref={messagesContainerRef}
        sx={{ 
          flexGrow: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100% - 70px)',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress 
              size={30} 
              sx={{
                color: isDark ? '#4fc3f7' : 'primary.main'
              }}
            />
          </Box>
        ) : messages.length === 0 ? (
          <Fade in={true} timeout={800}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%'
            }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : 'rgba(25, 118, 210, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                animation: 'emptyPulse 2s ease-in-out infinite',
                '@keyframes emptyPulse': {
                  '0%': {
                    transform: 'scale(1)',
                    boxShadow: isDark 
                      ? '0 0 0 0 rgba(79, 195, 247, 0.4)' 
                      : '0 0 0 0 rgba(25, 118, 210, 0.3)',
                  },
                  '50%': {
                    transform: 'scale(1.05)',
                    boxShadow: isDark 
                      ? '0 0 20px 10px rgba(79, 195, 247, 0)' 
                      : '0 0 20px 10px rgba(25, 118, 210, 0)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                    boxShadow: isDark 
                      ? '0 0 0 0 rgba(79, 195, 247, 0)' 
                      : '0 0 0 0 rgba(25, 118, 210, 0)',
                  },
                },
              }}>
                <SendIcon sx={{ 
                  fontSize: 36, 
                  color: isDark ? '#4fc3f7' : 'primary.main',
                  opacity: 0.8
                }} />
              </Box>
              <Typography 
                variant="body1" 
                sx={{
                  color: isDark ? '#bdc3c7' : 'text.secondary',
                  fontWeight: 500,
                  mb: 0.5
                }}
              >
                Aucun message
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: isDark ? '#7f8c8d' : 'text.disabled',
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}
              >
                Lancez la conversation !
              </Typography>
            </Box>
          </Fade>
        ) : (
          <List sx={{ width: '100%', p: 0, mb: 2 }}>
            {
              messages.map((msg, index) => {
                const isCurrentUser = msg.userId === user;
                return (
                  <Fade in={true} key={index} timeout={300}>
                    <Box sx={{ 
                      mb: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                    }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          maxWidth: '80%',
                        }}
                      >
                        <Box sx={{ 
                          mb: 0.5, 
                          display: 'flex', 
                          alignItems: 'center',
                          justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                          px: 1,
                        }}>
                          <Typography 
                            component="span" 
                            variant="caption" 
                            sx={{ 
                              mr: isCurrentUser ? 1 : 0, 
                              ml: isCurrentUser ? 0 : 1, 
                              order: isCurrentUser ? 1 : 2,
                              color: isDark ? '#95a5a6' : 'text.secondary'
                            }}
                          >
                            {formatDate(msg.timestamp)}
                          </Typography>
                          <Typography 
                            component="span" 
                            variant="subtitle2" 
                            fontWeight="500" 
                            sx={{ 
                              order: isCurrentUser ? 2 : 1,
                              color: isDark ? '#4fc3f7' : 'primary.main'
                            }}
                          >
                            <GetUserAvatar matricule={msg.userId} defaultValue="..." />
                          </Typography>
                        </Box>
                        
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            bgcolor: isCurrentUser 
                              ? (isDark ? '#4fc3f7' : 'primary.light')
                              : (isDark ? '#22303e' : 'white'),
                            color: isCurrentUser 
                              ? (isDark ? '#000' : 'primary.contrastText')
                              : (isDark ? '#ecf0f1' : 'text.primary'),
                            borderRadius: isCurrentUser ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
                            boxShadow: isDark 
                              ? '0 1px 2px rgba(0,0,0,0.3)' 
                              : '0 1px 2px rgba(0,0,0,0.08)',
                            position: 'relative',
                            zIndex: 1,
                            border: '1px solid',
                            borderColor: isCurrentUser 
                              ? (isDark ? '#4fc3f7' : 'primary.light')
                              : (isDark ? '#2d3f4c' : 'rgba(0,0,0,0.08)'),
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              wordBreak: 'break-word', 
                              lineHeight: 1.5,
                              color: 'inherit'
                            }}
                          >
                            {msg.message}
                          </Typography>
                        </Paper>
                      </Box>
                    </Box>
                  </Fade>
                );
              })
            }
            <div ref={messagesEndRef} />
          </List>
        )}
      </Box>

      {/* Indicateur d'écoute du micro */}
      {(isListening || isTranscribing) && (
        <Box sx={{
          position: 'absolute',
          bottom: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 11,
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: listenError ? 0 : 1,
            p: 0.75,
            borderRadius: '20px',
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
            backdropFilter: 'blur(8px)',
            border: `1px solid ${isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)'}`,
            boxShadow: isDark
              ? '0 2px 8px rgba(244, 67, 54, 0.15)'
              : '0 2px 8px rgba(244, 67, 54, 0.08)',
          }}>
            <Box sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#f44336',
              mr: 1,
              animation: isListening ? 'recordingPulse 1.5s infinite' : 'none',
              '@keyframes recordingPulse': {
                '0%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.3)', opacity: 0.7 },
                '100%': { transform: 'scale(1)', opacity: 1 },
              },
            }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                color: isDark ? '#ff8a80' : '#d32f2f',
                fontWeight: 500,
                letterSpacing: '0.02em',
              }}
            >
              {isListening ? 'Écoute en cours...' : 'Transcription en cours...'}
            </Typography>
          </Box>
        </Box>
      )}

      {listenError && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 11,
            width: '90%'
          }}
        >
          <Alert
            severity="error"
            sx={{
              borderRadius: '12px',
              backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)',
              color: isDark ? '#ff8a80' : 'error.main',
              '& .MuiAlert-icon': {
                color: isDark ? '#ff8a80' : 'error.main'
              }
            }}
          >
            {listenError}
          </Alert>
        </Box>
      )}

      {/* Zone d'écriture fixée en bas */}
      <Paper
        component="form"
        onSubmit={handleSendMessage}
        elevation={3}
        sx={{
          p: 1.5,
          backgroundColor: isDark ? '#22303e' : 'white',
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Votre message..."
          size="small"
          multiline
          maxRows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(e);
            }
          }}
          sx={{
            mr: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '25px',
              backgroundColor: isDark ? '#1a252f' : '#f5f7f9',
              color: isDark ? '#ecf0f1' : 'inherit',
              transition: 'all 0.2s',
              '&:hover, &.Mui-focused': {
                backgroundColor: isDark ? '#22303e' : '#fff',
                boxShadow: isDark
                  ? '0 0 0 2px rgba(79, 195, 247, 0.1)'
                  : '0 0 0 2px rgba(25, 118, 210, 0.1)',
              },
              '& fieldset': {
                borderColor: 'transparent',
              },
              '&:hover fieldset': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              },
              '&.Mui-focused fieldset': {
                borderColor: isDark ? '#4fc3f7' : 'primary.main',
              },
            },
            '& .MuiInputBase-input': {
              color: isDark ? '#ecf0f1' : 'inherit',
              '&::placeholder': {
                color: isDark ? '#95a5a6' : 'inherit',
                opacity: 1
              }
            }
          }}
        />
        <IconButton
          onClick={toggleMicrophone}
          disabled={!guardiaAccess.hasAccess || (isTranscribing && !isListening)}
          sx={{
            p: 1,
            mr: 1,
            borderRadius: '50%',
            backgroundColor: !guardiaAccess.hasAccess
              ? (isDark ? '#555' : '#ccc')
              : isListening
              ? (isDark ? '#ff5722' : '#f44336')
              : (isDark ? '#2d3f4c' : 'grey.100'),
            color: !guardiaAccess.hasAccess
              ? (isDark ? '#7f8c8d' : 'grey.400')
              : isListening
              ? '#fff'
              : (isDark ? '#95a5a6' : 'grey.600'),
            transition: 'all 0.2s ease',
            boxShadow: isListening ? '0 0 0 2px rgba(244, 67, 54, 0.3)' : 'none',
            animation: isListening ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.3)',
              },
              '50%': {
                transform: 'scale(1.1)',
                boxShadow: '0 0 0 4px rgba(244, 67, 54, 0.2)',
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.3)',
              },
            },
            '&:hover': !guardiaAccess.hasAccess ? {} : {
              backgroundColor: isListening
                ? (isDark ? '#d84315' : '#d32f2f')
                : (isDark ? '#5a6c7d' : 'grey.200'),
              transform: isListening ? 'scale(1.05)' : 'scale(1.05)',
            },
            '&.Mui-disabled': {
              backgroundColor: isDark ? '#555' : '#ccc',
              color: isDark ? '#7f8c8d' : 'grey.400'
            }
          }}
        >
          {isListening ? <MicOffIcon /> : (isTranscribing ? <CircularProgress size={18} sx={{ color: isDark ? '#95a5a6' : 'grey.600' }} /> : <MicIcon />)}
        </IconButton>
        <IconButton
          type="submit"
          disabled={!message.trim()}
          sx={{
            p: 1,
            borderRadius: '50%',
            backgroundColor: message.trim()
              ? (isDark ? '#4fc3f7' : 'primary.main')
              : (isDark ? '#2d3f4c' : 'grey.100'),
            color: message.trim()
              ? (isDark ? '#000' : 'white')
              : (isDark ? '#7f8c8d' : 'grey.400'),
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: message.trim()
                ? (isDark ? '#29b6f6' : 'primary.dark')
                : (isDark ? '#5a6c7d' : 'grey.200'),
              transform: message.trim() ? 'scale(1.05)' : 'none',
            },
          }}
        >
          <SendIcon />
        </IconButton>
        </Box>
      </Paper>
    </Box>
  );
}

export default Chat;



